import React, { useEffect, useState } from "react";
import { Autocomplete, Box, Checkbox, Chip } from "@mui/material";
import TextField from "../../../../components/TextField";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { getClients } from "../../../../api/resources/clients";
import { searchComponent } from "../../../../components/helpers/componentUtil";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const clientId = (client: any) =>
  typeof client === "object" ? client?.value : client;

interface ClientSelectionProps {
  selectedClients: any[];
  setSelectedClients: React.Dispatch<React.SetStateAction<any[]>>;
  formValues: any;
  setFormValues: React.Dispatch<React.SetStateAction<any>>;
}

const ClientSelection: React.FC<ClientSelectionProps> = ({
  selectedClients,
  setSelectedClients,
  setFormValues,
}) => {
  const [clientOptions, setClientOptions] = useState([]);
  const [searchClientString, setSearchClientString] = useState("");

  useEffect(() => {
    searchComponent(searchClientString, handleSearchClient);
  }, [searchClientString, searchComponent]);

  const handleSearchClient = () => {
    getClients(0, 15, null, searchClientString).then((result) => {
      const clientsOptions = result?.data.content.map((client) => {
        const name =
          client.type === "BUSINESS"
            ? client.companyName
            : client.firstName + " " + client.lastName;
        return { title: name, value: client.id };
      });
      setClientOptions(clientsOptions);
    });
  };

  // The API stores formValues.selectedClients as client ids, so never put options or nulls there.
  const updateSelectedClients = (clients: any[]) => {
    const validClients = clients.filter((client) => clientId(client) != null);
    setSelectedClients(validClients);
    setFormValues((prevValues: any) => ({
      ...prevValues,
      selectedClients: validClients.map(clientId),
    }));
  };

  const handleSelectClient = (option: any) => {
    const isSelected = selectedClients.some(
      (sc) => clientId(sc) === option.value,
    );
    updateSelectedClients(
      isSelected
        ? selectedClients.filter((sc) => clientId(sc) !== option.value)
        : [...selectedClients, option],
    );
  };

  const handleRemoveClient = (id: number) => {
    updateSelectedClients(
      selectedClients.filter((client) => clientId(client) !== id),
    );
  };

  return (
    <Box sx={{ mr: "0.5rem" }}>
      <Autocomplete
        multiple
        id="selectedClients"
        onChange={(event, value) => updateSelectedClients(value)}
        value={selectedClients}
        limitTags={2}
        disableCloseOnSelect
        options={clientOptions}
        getOptionLabel={(option) => option?.title ?? ""}
        defaultValue={[]}
        size="small"
        renderInput={(params) => (
          <TextField
            id="client"
            params={params}
            minWidth={420}
            maxWidth={420}
            label="Client"
            onChange={(e: any) => setSearchClientString(e.target.value)}
          />
        )}
        renderTags={(value: readonly string[], getTagProps) =>
          value?.map((option: any, index: number) => {
            if (option == null) return null;
            const { key } = getTagProps({ index });
            return (
              <Chip
                label={option.title}
                key={key}
                onDelete={() => handleRemoveClient(option.value)}
                size="small"
                sx={{ mr: "4px", mb: "4px" }}
              />
            );
          })
        }
        renderOption={(props, option) => {
          const isSelected = selectedClients.some(
            (sc) => clientId(sc) === option.value,
          );

          return (
            <li
              {...props}
              style={{ padding: "0rem" }}
              onClick={() => handleSelectClient(option)}
            >
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 4 }}
                checked={isSelected}
              />
              {option.title}
            </li>
          );
        }}
      />
    </Box>
  );
};

export default ClientSelection;
